import { useRef } from "react";
import { IconHash, IconSparkles } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import NavRoomsIcon from "../../assets/icons/nav/nav-rooms.svg?react";
import { cn } from "../../lib/utils";
import {
  NewContentDot,
  TAB_BAR_LAYOUTS,
  TabButton,
  type TabBarLayout,
} from "../ui/TabButton";
import { DiscoveryInterestsSheet } from "./DiscoveryInterestsSheet";
import { ScrollArrowButton } from "../ui/ScrollArrowButton";
import { useHorizontalOverflow } from "../../hooks/use-horizontal-overflow";
import Skeleton from "../skeleton/Skeleton";

// What the Discovery tab bar can have selected. A topic and a room are mutually
// exclusive (single chip active at a time); the page owns the URL encoding.
type DiscoveryTabSelection =
  | { kind: "for-you" }
  | { kind: "topic"; topicUid: string }
  | { kind: "room"; roomId: string };

function RoomFilledIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="13.5" y="14" width="7" height="7" rx="1" />
      <rect x="2.5" y="14" width="7" height="7" rx="1" />
      <rect x="2.5" y="3" width="7" height="7" rx="1" />
      <rect
        x="16.9834"
        y="2.41421"
        width="6.25"
        height="6.25"
        rx="1"
        transform="rotate(45 16.9834 2.41421)"
      />
    </svg>
  );
}

/**
 * How much of the last chip the "next" button leaves in sight: the row's 8px
 * gap plus enough of the chip to read as a chip, as in `PracticeFeedRail`.
 */
const NEXT_CHIP_PEEK = 48;

type DiscoveryTabBarProps = {
  // User's selected interests, shown before rooms (Topics chantier). The page
  // resolves the display name via getItemTranslation.
  topics: ReadonlyArray<{ uid: string; name: string }>;
  rooms: ReadonlyArray<{ id: string; name: string; badge: boolean | null }>;
  /**
   * `null` lights no chip: a list the bar has no chip for (desktop's saved and
   * recently read papers, reached by URL).
   */
  selection: DiscoveryTabSelection | null;
  onSelect: (selection: DiscoveryTabSelection) => void;
  layout: TabBarLayout;
};

function DiscoveryTabBar({
  topics,
  rooms,
  selection,
  onSelect,
  layout,
}: DiscoveryTabBarProps): React.JSX.Element {
  const { t } = useTranslation();
  const style = TAB_BAR_LAYOUTS[layout];
  // The row the desktop "next" button scrolls: UI machinery, not page state.
  const rowRef = useRef<HTMLDivElement>(null);
  const overflows = useHorizontalOverflow(rowRef);
  const chips = (
    <>
      <TabButton
        className={cn(style.chip)}
        active={selection !== null && selection.kind === "for-you"}
        icon={<IconSparkles size={16} stroke={style.iconStroke} />}
        onClick={() => {
          onSelect({ kind: "for-you" });
        }}
      >
        {t("For You")}
      </TabButton>
      {topics.map((topic) => (
        <TabButton
          key={topic.uid}
          className={cn(style.chip)}
          active={
            selection !== null &&
            selection.kind === "topic" &&
            selection.topicUid === topic.uid
          }
          icon={<IconHash size={16} stroke={style.iconStroke} />}
          onClick={() => {
            onSelect({ kind: "topic", topicUid: topic.uid });
          }}
        >
          {topic.name}
        </TabButton>
      ))}
      {rooms.map((room) => (
        <TabButton
          key={room.id}
          className={cn(style.chip)}
          active={
            selection !== null &&
            selection.kind === "room" &&
            selection.roomId === room.id
          }
          icon={
            <>
              {room.badge === true && <NewContentDot />}
              <NavRoomsIcon className="size-4" />
            </>
          }
          activeIcon={
            <>
              {room.badge === true && <NewContentDot />}
              <RoomFilledIcon className="size-4 text-ds-slate-50" />
            </>
          }
          onClick={() => {
            onSelect({ kind: "room", roomId: room.id });
          }}
        >
          {room.name}
        </TabButton>
      ))}
    </>
  );

  // Mobile scrolls the settings chip away with the others. Desktop pins it, as
  // the Figma bar and `PracticeFeedRail` do, fogs the scrolling edge and ends
  // on the Figma "next" `IconButton` while chips hide past the end.
  if (layout === "mobile")
    return (
      <div
        className={cn(
          "flex w-full items-center gap-2 overflow-x-auto pt-3 scrollbar-none [&::-webkit-scrollbar]:hidden",
          style.rowStart,
          style.rowEnd
        )}
      >
        <DiscoveryInterestsSheet layout={layout} />
        {chips}
      </div>
    );
  return (
    <div className="flex items-center gap-2 pt-3">
      <DiscoveryInterestsSheet layout={layout} />
      <div
        ref={rowRef}
        className="scroll-fog-x flex min-w-0 flex-1 items-center gap-2 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden"
      >
        {chips}
      </div>
      {overflows ? (
        <ScrollArrowButton
          direction="next"
          onClick={() => {
            const row = rowRef.current;
            if (row === null)
              throw new Error("should not happen: the tab row is unmounted");
            row.scrollBy({
              left: row.clientWidth - NEXT_CHIP_PEEK,
              behavior: "smooth",
            });
          }}
        />
      ) : null}
    </div>
  );
}

// Placeholder chip widths — the real chip count depends on the user's rooms and
// topics, which aren't known at boot, so a fixed representative set stands in.
const SKELETON_CHIP_WIDTHS = [96, 84, 110] as const;

/**
 * Loading silhouette for {@link DiscoveryTabBar}: the leading gear settings
 * square (size-10) then filter chips — all h-10 with an 8px (`rounded-lg`)
 * radius, not pills — sharing the real bar's `px-5 pt-3`.
 */
function DiscoveryTabBarSkeleton(): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 px-5 pt-3">
      <Skeleton className="rounded-lg" width={40} height={40} />
      {SKELETON_CHIP_WIDTHS.map((width) => (
        <Skeleton
          key={width}
          className="rounded-lg"
          width={width}
          height={40}
        />
      ))}
    </div>
  );
}

export {
  DiscoveryTabBar,
  DiscoveryTabBarSkeleton,
  type DiscoveryTabBarProps,
  type DiscoveryTabSelection,
};
